/**
 * A mail being written in the rich editor (#1113): its HTML, and the plain-text part sent beside it, so a client that
 * shows no HTML still reads the same words, lists and links.
 */
export type MailDraft = { html: string; text: string }

export function emptyDraft(): MailDraft {
    return { html: '', text: '' }
}

/** Nothing written yet: only blanks, whatever empty markup the editor holds. */
export function draftIsBlank(draft: MailDraft): boolean {
    return draft.text.trim() === ''
}

/** A plain draft from text, as a test or a prefilled field writes one. */
export function draftOfText(text: string): MailDraft {
    const html = text
        .split(/\n{2,}/)
        .map(block => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
        .join('')
    return { html: text.trim() === '' ? '' : html, text }
}

function escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

const BLOCKS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'PRE', 'UL', 'OL', 'BLOCKQUOTE', 'HR'])

/**
 * The plain-text part of a formatted mail: paragraphs a blank line apart, list items marked `-` or numbered, quotes
 * marked `>`, and each link written out after its words, as mail clients do when they show text alone.
 */
export function htmlToText(html: string): string {
    if (html.trim() === '') {
        return ''
    }
    const body = new DOMParser().parseFromString(html, 'text/html').body
    return blocksOf(body)
        .join('\n\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
}

/** Each block of `parent` as text, in order; inline runs between blocks join into one. */
function blocksOf(parent: Node): string[] {
    const blocks: string[] = []
    let inline = ''
    const flush = () => {
        if (inline.trim() !== '') {
            blocks.push(inline.trim())
        }
        inline = ''
    }
    for (const node of Array.from(parent.childNodes)) {
        if (node instanceof HTMLElement && BLOCKS.has(node.tagName)) {
            flush()
            const block = blockText(node)
            if (block.trim() !== '') {
                blocks.push(block)
            }
        } else {
            inline += inlineText(node)
        }
    }
    flush()
    return blocks
}

function blockText(element: HTMLElement): string {
    switch (element.tagName) {
        case 'UL':
        case 'OL': {
            const items = Array.from(element.children).filter(child => child.tagName === 'LI')
            return items
                .map((item, at) => {
                    const marker = element.tagName === 'OL' ? `${at + 1}. ` : '- '
                    const inner = blocksOf(item).join('\n')
                    return marker + inner.replace(/\n/g, `\n${' '.repeat(marker.length)}`)
                })
                .join('\n')
        }
        case 'BLOCKQUOTE':
            return blocksOf(element)
                .join('\n\n')
                .split('\n')
                .map(line => (line === '' ? '>' : `> ${line}`))
                .join('\n')
        case 'HR':
            return '---'
        case 'PRE':
            return element.textContent ?? ''
        case 'DIV':
            return blocksOf(element).join('\n\n')
        default:
            return Array.from(element.childNodes).map(inlineText).join('').trim()
    }
}

function inlineText(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent ?? '').replace(/\s+/g, ' ')
    }
    if (!(node instanceof HTMLElement)) {
        return ''
    }
    if (node.tagName === 'BR') {
        return '\n'
    }
    const inner = Array.from(node.childNodes).map(inlineText).join('')
    if (node.tagName === 'A') {
        const href = node.getAttribute('href') ?? ''
        const shown = href.replace(/^mailto:/, '')
        return href && inner.trim() !== shown ? `${inner} (${shown})` : inner
    }
    return inner
}
